import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useFeedback } from '../../contexts/FeedbackContext';
import { EyeIcon, EyeOffIcon, MailIcon, UserIcon } from '../../components/common/Icons';
import LanguageSelector from '../../components/common/LanguageSelector';
import { requiredFieldMessage, requiredFieldsMessage } from '../../utils/validationMessages';
import styles from './AuthPages.module.css';

const LoginPage = () => {
  const { login } = useAuth();
  const { notify } = useFeedback();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', remember: false });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = {};
    const missingLabels = [];
    if (!form.email.trim()) {
      errors.email = requiredFieldMessage(t, t('auth.email'));
      missingLabels.push(t('auth.email'));
    }
    if (!form.password.trim()) {
      errors.password = requiredFieldMessage(t, t('auth.password'));
      missingLabels.push(t('auth.password'));
    }
    setFieldErrors(errors);
    if (missingLabels.length) {
      notify({
        variant: 'danger',
        title: t('feedback.loginErrorTitle'),
        message: requiredFieldsMessage(t, missingLabels),
      });
      return;
    }
    setLoading(true);
    try {
      await login(form.email, form.password, { remember: form.remember });
      navigate('/dashboard');
    } catch (err) {
      notify({
        variant: 'danger',
        title: t('feedback.loginErrorTitle'),
        message: err.response?.data?.error || err.message || t('feedback.loginErrorMessage'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginContent}>
      <h1 className="text-center text-2xl font-bold text-slate-900">{t('auth.loginTitle')}</h1>
      <p className="mt-2 text-center text-slate-600">{t('auth.subtitle')}</p>
      <LanguageSelector className={`${styles.loginLanguage} mt-5`} />

      <form onSubmit={handleSubmit} className={`${styles.loginForm} mt-8 space-y-5`} noValidate>
        <AuthField
          label={t('auth.email')}
          type="email"
          value={form.email}
          onChange={(value) => {
            setForm({ ...form, email: value });
            setFieldErrors((current) => ({ ...current, email: '' }));
          }}
          placeholder={t('auth.emailPlaceholder')}
          icon={<MailIcon className="h-5 w-5" />}
          autoComplete="email"
          error={fieldErrors.email}
        />
        <AuthField
          label={t('auth.password')}
          type="password"
          value={form.password}
          onChange={(value) => {
            setForm({ ...form, password: value });
            setFieldErrors((current) => ({ ...current, password: '' }));
          }}
          placeholder="••••••••"
          icon={<UserIcon className="h-5 w-5" />}
          autoComplete="current-password"
          revealable
          error={fieldErrors.password}
        />

        <div className="flex items-center justify-between gap-4 text-sm">
          <label className="inline-flex min-h-11 items-center gap-2 text-slate-600">
            <input
              type="checkbox"
              checked={form.remember}
              onChange={(event) => setForm({ ...form, remember: event.target.checked })}
              className="h-4 w-4 rounded border-slate-300 text-sky-500 focus:ring-sky-200"
            />
            {t('auth.remember')}
          </label>
          <Link to="/forgot-password" className="inline-flex min-h-11 items-center font-semibold text-sky-500 hover:text-sky-600">
            {t('auth.forgot')}
          </Link>
        </div>

        <button
          className="inline-flex h-12 w-full items-center justify-center rounded-lg bg-sky-500 px-5 font-bold text-white shadow-soft transition hover:bg-sky-600 disabled:opacity-60"
          type="submit"
          disabled={loading}
        >
          {loading ? t('common.processing') : t('auth.loginButton')}
        </button>
      </form>

      <Link
        to="/register"
        className={`${styles.registerCta} mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-lg border border-sky-500 bg-sky-50 px-5 text-center text-sm font-bold text-sky-700 transition hover:bg-sky-100 focus:outline-none focus:ring-4 focus:ring-sky-100`}
      >
        {t('auth.signUpButton')}
      </Link>
    </div>
  );
};

export const AuthField = ({ label, icon, value, onChange, revealable = false, type, error, ...props }) => {
  const [visible, setVisible] = useState(false);
  const inputType = revealable && type === 'password' ? (visible ? 'text' : 'password') : type;

  return (
    <label className="block">
      <span className="mb-2 block text-sm font-semibold text-slate-900">{label}</span>
      <span className={`flex h-10 items-center gap-3 rounded-lg border bg-slate-50 px-3 text-slate-500 transition focus-within:border-sky-300 focus-within:ring-4 focus-within:ring-sky-100 ${error ? 'border-red-300' : 'border-slate-200'}`}>
        {icon}
        <input
          className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-500"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          type={inputType}
          {...props}
        />
        {revealable && type === 'password' && (
          <button
            type="button"
            aria-label={visible ? 'Ocultar senha' : 'Mostrar senha'}
            className="rounded p-1 text-slate-500 transition hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-200"
            onClick={(event) => {
              event.preventDefault();
              setVisible((current) => !current);
            }}
          >
            {visible ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
          </button>
        )}
      </span>
      {error && <span className="mt-1 block text-xs font-semibold text-red-600">{error}</span>}
    </label>
  );
};

export default LoginPage;
